import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  X, Loader2, Download, Printer, FileDown, ExternalLink, FileText, HardDrive,
} from "lucide-react";
import { api } from "../lib/api";
import { formatBytes } from "../lib/format";

/**
 * Blocco 3 · C · Modal di anteprima documento.
 *
 * - PDF → iframe
 * - Immagine → tag <img>
 * - Altri formati → icona + nome + CTA "Scarica per aprire"
 *
 * Fullscreen su mobile, centrale con card su desktop.
 */
export default function DocumentPreview({ docId, open, onClose }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    if (!open || !docId) return;
    setLoading(true);
    let cancelled = false;
    api.docsGet(docId)
      .then((d) => { if (!cancelled) setDoc(d); })
      .catch(() => toast.error("Errore caricamento anteprima"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [docId, open]);

  // Reset when closed to free memory & avoid stale content on re-open.
  useEffect(() => {
    if (!open) { setDoc(null); setLoading(false); }
  }, [open]);

  if (!open) return null;

  const isImage = doc && (doc.mime_type || "").startsWith("image/");
  const isPdf = doc && doc.mime_type === "application/pdf";
  const previewUrl = doc?.preview_url ? `${backendUrl}${doc.preview_url}` : null;

  const doPrint = () => {
    if (!previewUrl) return;
    const w = window.open(previewUrl, "_blank", "noopener,noreferrer");
    if (!w) { toast.error("Il browser ha bloccato la finestra di stampa"); return; }
    setTimeout(() => { try { w.focus(); w.print(); } catch {} }, isPdf ? 1200 : 400);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex sm:items-center sm:justify-center"
      onClick={onClose}
      data-testid="doc-preview-modal"
    >
      <div
        className="bg-white w-full sm:max-w-4xl h-full sm:h-auto sm:max-h-[90vh] sm:rounded-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: "var(--pxos-border)" }}>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate" data-testid="doc-preview-title">
              {loading ? "Caricamento…" : doc?.title || doc?.filename || "Anteprima documento"}
            </div>
            {doc && (
              <div className="text-[10px] opacity-60 flex items-center gap-1 mt-0.5">
                <HardDrive size={9}/> {formatBytes(doc.size_bytes)} · {doc.mime_type}
              </div>
            )}
          </div>
          {doc && (
            <>
              {(isPdf || isImage) && (
                <button onClick={doPrint} className="rounded-full border px-2.5 py-1 text-[11px] inline-flex items-center gap-1 hover:bg-black/5"
                        data-testid="doc-preview-print" title="Stampa">
                  <Printer size={11}/> Stampa
                </button>
              )}
              {isImage && (
                <a href={`${backendUrl}/api/documents/${doc.id}/download?format=pdf`}
                   target="_blank" rel="noreferrer"
                   className="rounded-full border px-2.5 py-1 text-[11px] inline-flex items-center gap-1 hover:bg-black/5"
                   data-testid="doc-preview-download-pdf" title="Scarica come PDF">
                  <FileDown size={11}/> PDF
                </a>
              )}
              <a href={`${backendUrl}/api/documents/${doc.id}/download`}
                 target="_blank" rel="noreferrer"
                 className="rounded-full border px-2.5 py-1 text-[11px] inline-flex items-center gap-1 hover:bg-black/5"
                 data-testid="doc-preview-download" title="Scarica originale">
                <Download size={11}/> Scarica
              </a>
              <Link
                to={`/documenti/${doc.id}`}
                onClick={onClose}
                className="rounded-full border px-2.5 py-1 text-[11px] inline-flex items-center gap-1 hover:bg-black/5"
                data-testid="doc-preview-open-detail"
                title="Apri dettaglio completo"
              >
                <ExternalLink size={11}/> Dettaglio
              </Link>
            </>
          )}
          <button onClick={onClose} className="p-1.5 hover:bg-black/5 rounded" data-testid="doc-preview-close" aria-label="Chiudi">
            <X size={18}/>
          </button>
        </div>

        <div className="flex-1 min-h-0 bg-neutral-50 overflow-auto">
          {loading || !doc ? (
            <div className="h-full grid place-items-center py-16 opacity-70">
              <Loader2 size={20} className="animate-spin"/>
            </div>
          ) : !previewUrl ? (
            <UnsupportedFallback doc={doc} backendUrl={backendUrl}/>
          ) : isImage ? (
            <img
              src={previewUrl}
              alt={doc.title || doc.filename}
              className="max-w-full max-h-[80vh] mx-auto block"
              data-testid="doc-preview-image"
            />
          ) : isPdf ? (
            <iframe
              src={previewUrl}
              title={doc.title || doc.filename}
              className="w-full h-[80vh] border-0"
              data-testid="doc-preview-pdf"
            />
          ) : (
            <UnsupportedFallback doc={doc} backendUrl={backendUrl}/>
          )}
        </div>
      </div>
    </div>
  );
}

function UnsupportedFallback({ doc, backendUrl }) {
  return (
    <div className="grid place-items-center py-20 px-6 text-center" data-testid="doc-preview-unsupported">
      <div className="max-w-sm">
        <FileText size={40} className="mx-auto opacity-40"/>
        <div className="mt-3 font-semibold">{doc.filename}</div>
        <div className="text-xs opacity-70 mt-1">{formatBytes(doc.size_bytes)} · {doc.mime_type}</div>
        <div className="text-sm opacity-70 mt-3">
          Anteprima non supportata per questo formato. Scarica il file per aprirlo con l'app dedicata.
        </div>
        <a
          href={`${backendUrl}/api/documents/${doc.id}/download`}
          target="_blank" rel="noreferrer"
          className="pxos-btn-primary text-xs inline-flex items-center gap-1.5 mt-4"
        >
          <Download size={12}/> Scarica per aprire
        </a>
      </div>
    </div>
  );
}
